import React, { useContext, useEffect, useState } from "react";
import {
  AppBar,
  Toolbar,
  IconButton,
  InputBase,
  MenuItem,
  Menu,
  Box,
  useTheme,
  Tooltip,
  Badge,
  Stack,
  Popover,
  List,
  ListItem,
  Typography,
  Divider,
  ListItemText,
  ListItemIcon, 
  Icon,
  CircularProgress,
  Dialog,
  DialogContent,
  Alert,
  Button,
  DialogTitle,
  DialogActions,
} from "@mui/material";
import {
  Search as SearchIcon,
  AccountCircle,
  Notifications as NotificationsIcon,
  Article as ReportIcon,
  MoreVert as MoreIcon,
  Brightness4 as DarkModeIcon,
  Brightness7 as LightModeIcon,
  Settings as SettingsIcon,
  Description as UserManualIcon,
  GetApp as MobileAppIcon,
  SwitchAccessShortcutOutlined,
  ArrowCircleRightRounded,
  SwapVertRounded,
  Mail as MailIcon,
} from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate } from "react-router-dom";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import { styled, alpha } from "@mui/material/styles";
import { ColorModeContext, tokens } from "../../theme";
import { notificationsAndCount } from "../../data/Axios/queries";
import { useNotificationsRefetchSignal } from "../../context/NotificationHubContext";
import {
  fetchDataEngine,
  graphqlMutation,
  graphqlQuery,
} from "../../data/Axios/DynamicService";
import { companyManagementUrl, downloadsUrl, notificationservice, salesManagement } from "../../config";
import { jwtDecode } from "jwt-decode";
import dayjs from "dayjs";
import axios from "axios";
import NotificationBell from "../../components/DashboardNotifications/NotificationBell";

// Styled Search
const base_url = salesManagement.uri;
const Search = styled("div")(({ theme }) => ({
  position: "relative",
  borderRadius: theme.shape.borderRadius * 2, // More rounded
  backgroundColor: alpha(theme.palette.common.white, 0.15),
  "&:hover": {
    backgroundColor: alpha(theme.palette.common.white, 0.25),
  },
  marginRight: theme.spacing(2),
  marginLeft: 0,
  width: "100%",
  [theme.breakpoints.up("sm")]: {
    marginLeft: theme.spacing(3),
    width: "auto",
  },
  transition: theme.transitions.create("background-color", {
    duration: theme.transitions.duration.shorter,
  }),
  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)", // Add subtle shadow
}));

const SearchIconWrapper = styled("div")(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: "100%",
  position: "absolute",
  pointerEvents: "none",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: "inherit",
  "& .MuiInputBase-input": {
    padding: theme.spacing(1, 1, 1, 0),
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create("width"),
    width: "100%",
    [theme.breakpoints.up("md")]: {
      width: "24ch",
    },
  },
}));

// Trial-days badge, styled with the app's own brand-blue token (colors.primary[500])
// rather than MUI's generic default palette, to stay visually consistent with the
// sidebar/tables which already use this same token as the brand accent.
const TrialDaysBadge = styled(Box)(({ theme }) => {
  const colors = tokens(theme.palette.mode);
  return {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(0.5, 1.5),
    borderRadius: theme.shape.borderRadius * 2,
    backgroundColor: alpha(colors.primary[500], 0.15),
    border: `1px solid ${alpha("#fff", 0.4)}`,
    marginRight: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      marginRight: theme.spacing(1),
      padding: theme.spacing(0.5, 1),
    },
  };
});

// Premium upsell button, green-to-blue gradient using the app's own accent tokens.
const PremiumButton = styled(Button)(({ theme }) => {
  const colors = tokens(theme.palette.mode);
  return {
    textTransform: "none",
    fontWeight: "bold",
    padding: theme.spacing(0.5, 1.5),
    borderRadius: theme.shape.borderRadius * 2,
    background: `linear-gradient(45deg, ${colors.greenAccent[500]} 0%, ${colors.primary[500]} 100%)`,
    color: "#fff",
    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
    "&:hover": {
      background: `linear-gradient(45deg, ${colors.primary[500]} 0%, ${colors.greenAccent[600]} 100%)`,
      boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
    },
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(0.3, 1),
      fontSize: "0.75rem",
    },
  };
});

const CampaignBanner = styled(Box)(({ theme }) => ({
  position: "fixed",
  top: 0,
  left: "50%", // Center horizontally
  transform: "translateX(-50%)", // Adjust for centering
  width: "800px", // Fixed width (adjust as needed)
  maxWidth: "90%", // Ensure it doesn’t overflow on small screens
  zIndex: 1300,
  backgroundColor: theme.palette.success.light,
  color: theme.palette.success.contrastText,
  padding: theme.spacing(1),
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
  animation: "slideDown 0.5s ease-in-out",
  "@keyframes slideDown": {
    from: { transform: "translateY(-100%) translateX(-50%)" }, // Include X transform in animation
    to: { transform: "translateY(0) translateX(-50%)" },
  },
  // Responsive adjustments
  [theme.breakpoints.down("sm")]: {
    flexDirection: "column",
    textAlign: "center",
    padding: theme.spacing(0.5),
    width: "300px", // Smaller width on mobile
  },
}));

// Notification bodies are JSON-encoded strings (backend-wide convention).
// Prefer a human-readable `message` field when present; fall back to the
// raw body for notification types that don't follow that shape.
const getNotificationDisplayText = (notification) => {
  try {
    const parsed = JSON.parse(notification.body);
    return parsed?.message || notification.body;
  } catch {
    return notification.body;
  }
};

const NOTIFICATION_ICON_BY_TYPE = {
  CrbMonthlyReport: ReportIcon,
};

const getNotificationIcon = (notificationType) =>
  NOTIFICATION_ICON_BY_TYPE[notificationType] || NotificationsIcon;

// Relative time ("3 hours ago") isn't reliable here: createdDate comes from
// the backend's clock and can be out of sync with the viewer's local clock
// by more than a few minutes, which would misorder or misdescribe entries.
// Showing the absolute timestamp is honest regardless of that skew.
//
// The backend sends createdDate as already-EAT wall-clock time but with a
// trailing "Z" (UTC marker), so letting dayjs parse it as UTC and convert to
// local double-shifts it. Strip the "Z" so it's read as a plain local
// timestamp with no timezone conversion applied.
const formatNotificationTimestamp = (createdDate) =>
  dayjs(createdDate.replace(/Z$/, "")).format("MMM D, YYYY h:mm A");

// Shared by the desktop and mobile notification popovers so both stay in
// sync instead of drifting as two copy-pasted blocks.
const NotificationsPopoverContent = ({ notifications, colors, theme }) => {
  const items = notifications.notifications || [];

  return (
    <Box
      sx={{
        width: 340,
        borderRadius: "0.75rem",
        bgcolor:
          theme.palette.mode === "light"
            ? colors.blueAccent[900]
            : colors.grey[600],
        overflow: "hidden",
      }}
    >
      <Typography
        sx={{ fontSize: 14, color: colors.greenAccent[400], px: 2, pt: 2, pb: 1 }}
        variant="h6"
      >
        Notifications
      </Typography>

      {items.length === 0 ? (
        <Typography
          variant="body2"
          sx={{ px: 2, py: 3, textAlign: "center", opacity: 0.7 }}
        >
          You're all caught up — no notifications yet.
        </Typography>
      ) : (
        <List dense sx={{ maxHeight: 360, overflowY: "auto", py: 0 }}>
          {items.map((notification) => {
            const Icon = getNotificationIcon(notification.notificationType);
            return (
              <React.Fragment key={notification.notificationId}>
                <ListItem alignItems="flex-start" sx={{ px: 2, py: 1.25 }}>
                  <ListItemIcon sx={{ minWidth: 36, mt: "2px" }}>
                    <Icon
                      fontSize="small"
                      sx={{
                        color: notification.isRead
                          ? colors.grey[400]
                          : colors.greenAccent[400],
                      }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Tooltip title={getNotificationDisplayText(notification)}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: notification.isRead ? 400 : 600,
                            display: "-webkit-box",
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                          }}
                        >
                          {getNotificationDisplayText(notification)}
                        </Typography>
                      </Tooltip>
                    }
                    secondary={
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        component="span"
                      >
                        {formatNotificationTimestamp(notification.createdDate)}
                      </Typography>
                    }
                  />
                </ListItem>
                <Divider sx={{ borderColor: theme.palette.divider }} />
              </React.Fragment>
            );
          })}
        </List>
      )}
    </Box>
  );
};

const Topbar = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const colorMode = useContext(ColorModeContext);
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [mobileMoreAnchorEl, setMobileMoreAnchorEl] = React.useState(null);
  const [settingsAnchorEl, setSettingsAnchorEl] = React.useState(null);
  const [notifications, setNotifications] = useState({
    notifications: [],
    count: 0,
  });
  const [trialDays, setTrialDays] = useState(0);
  const decodedToken = jwtDecode(localStorage.token);
  const roleGroupId = parseInt(decodedToken.RoleGroupId); // Ensure this field exists in the token
  const [isLoading, setIsLoading] = useState(false); // Loading state
  const [error, setError] = useState(null); // Error state for dialog

  const userId = parseInt(decodedToken.Id);
  const companyId = parseInt(decodedToken.CompanyId);
  const [balances, setbalances] = useState({});

  const [showCampaign, setShowCampaign] = useState(true); // Campaign banner state

  const notificationsRefetchSignal = useNotificationsRefetchSignal();

  useEffect(() => {
    let isMounted = true;
    async function fetchAllData() {
      try {
        const notificationsResponse = await graphqlQuery(
          notificationsAndCount,
          notificationservice.uri
        );
          const trialdaysResponse = await graphqlQuery(
        `{
            trialDaysLeftbyCompanyId(company: { id: ${companyId} }) {
              error
              results
            }
          }
          `,
          companyManagementUrl.uri//diy
        );
        if (isMounted && notificationsResponse) {
          setNotifications(notificationsResponse);
        }
        if (trialdaysResponse) {
          setTrialDays(parseInt(trialdaysResponse.results));
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    }
    fetchAllData();
    return () => {
      isMounted = false;
    };
  }, [notificationsRefetchSignal]);

  const handleSettingsMenuOpen = (event) => {
    setSettingsAnchorEl(event.currentTarget);
  };

  // Handle Settings menu close
  const handleSettingsMenuClose = () => {
    setSettingsAnchorEl(null);
  };
  // Handle User Manual download
  const token = localStorage.getItem("token");
  const formattedToken = `Bearer ${token?.replace(/"/g, "")}`;
  const handleDownloadUserManual = async () => {
    setIsLoading(true); // Show loading animation
    setError(null); // Reset error state
    try {
      const response = await axios.get(downloadsUrl.file, {
        responseType: "blob",
        headers: {
          Authorization: formattedToken,
          "Content-Type": "application/json",
        },
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "MZW_User_Manual.pdf");
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Error downloading the user manual:", error);
      setError("Failed to download the user manual. Please try again later.");
    } finally {
      setIsLoading(false); // Hide loading animation
    }
  };

  const handleDownloadMobileApp = async () => {
    setIsLoading(true); // Show loading animation
    setError(null); // Reset error state
    try {
      const response = await axios.get(downloadsUrl.apk, {
        responseType: "blob",
        headers: {
          Authorization: formattedToken,
          "Content-Type": "application/json",
        },
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "MZW_Mobile_App.apk");
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Error downloading the mobile app:", error);
      setError("Failed to download the mobile app. Please try again later.");
    } finally {
      setIsLoading(false); // Hide loading animation
    }
  };
  
  const handleCloseErrorDialog = () => {
    setError(null);
  };
  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMobileMenuClose = () => {
    setMobileMoreAnchorEl(null);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    handleMobileMenuClose();
  };

  const handlePersonDetailsOpen = () => {
    navigate("/profile");
    setIsProfileOpen(true);
  };

  const handlePersonDetailsClose = () => {
    navigate("/super-admin-dashboard");
    setIsProfileOpen(false);
  };

  const handleIconClick = () => {
    if (isProfileOpen) {
      handlePersonDetailsClose();
    } else {
      handlePersonDetailsOpen();
    }
  };

  const handleMobileMenuOpen = (event) => {
    setMobileMoreAnchorEl(event.currentTarget);

  };
  const handleLogout = () => {
    // Save logo keys BEFORE clearing auth — they must survive logout/login.
    const logoKeysToKeep = ["uploadedLogoDataUrl", "filePath"];
    const preserved = {};
    logoKeysToKeep.forEach((k) => {
      const v = localStorage.getItem(k);
      if (v && v !== "null" && v !== "undefined") preserved[k] = v;
    });

    // Remove only auth-related keys — not the logo cache.
    [
      "token",
      "tokenExpiration",
      "decodedToken",
      "rights",
      "rightsforrbac",
      "systemAToken",
      "pendingAuth",
    ].forEach((k) => localStorage.removeItem(k));

    // Restore logo keys.
    Object.entries(preserved).forEach(([k, v]) => localStorage.setItem(k, v));

    if (caches) {
      caches.keys().then((names) => {
        names.forEach((name) => caches.delete(name));
      });
    }

    navigate("/login");
  };
  const handleMcbsWitch = () => {
    navigate("/MCB-SWITCH");
  };
  const handleNotificationClick = async (event) => {
    const newAnchorEl = anchorEl ? null : event.currentTarget;
    setAnchorEl(newAnchorEl);

    if (!anchorEl) {
      try {
        // Call the mutation to mark all notifications as read
        const result = await graphqlMutation(
          `mutation MarkNotificationsRead {
            markNotificationsRead {
              notificationId
              userId
              notificationType
              body
              templateId
              isRead
              createdDate
              companyId
              company
            }
          }`,
          notificationservice.uri
        );

        // Update the notifications state
        setNotifications((prevNotifications) => ({
          ...prevNotifications,
          notifications: prevNotifications.notifications.map(
            (notification) => ({ ...notification, isRead: true })
          ),
          count: 0,
        }));
      } catch (error) {
        console.error("Error marking notifications as read:", error);
        console.log(
          "Detailed Error Info:",
          error.response?.data || error.message
        );
      }
    }
  };

  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;
  const menuId = "primary-search-account-menu";
  const renderMenu = (
    <Menu
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      id={menuId}
      keepMounted
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      // open={isMenuOpen}
      onClose={handleMenuClose}
    >
      <MenuItem onClick={handleMenuClose}>Profile</MenuItem>
      <MenuItem onClick={handleMenuClose}>My account</MenuItem>
    </Menu>
  );

  const mobileMenuId = "primary-search-account-menu-mobile";
  const renderMobileMenu = (
    <Menu
      anchorEl={mobileMoreAnchorEl}
      anchorOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      id={mobileMenuId}
      keepMounted
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      //open={isMobileMenuOpen}
      open={Boolean(mobileMoreAnchorEl)}
      onClose={handleMobileMenuClose}
    >
      {trialDays > 0 && (
        <MenuItem sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="body2">
            {trialDays} {trialDays === 1 ? 'Day' : 'Days'} Left
          </Typography>
          <PremiumButton 
            size="small" 
            onClick={() => {
              handleMobileMenuClose();
            }}
          >
            Go Premium
          </PremiumButton>
        </MenuItem>
      )}
      <MenuItem  onClick={handleNotificationClick}>
        <IconButton size="large" aria-label="show 4 new mails" color="inherit">
          <Badge
            badgeContent={notifications.count}
            color="error"
            sx={{
              "& .MuiBadge-badge": {
                backgroundColor: colors.redAccent[500],
                color: "#fff",
              },
            }}
          >
            <MailIcon />
          </Badge>
        </IconButton>
        <p>Messages</p>
      </MenuItem>
      <MenuItem onClick={handleNotificationClick}>
        <IconButton
          size="large"
          aria-label="show 17 new notifications"
          color="inherit"
        >
          <Badge
            badgeContent={notifications.count}
            color="error"
            sx={{
              "& .MuiBadge-badge": {
                backgroundColor: colors.redAccent[500],
                color: "#fff",
              },
            }}
          >
            <NotificationsIcon />
          </Badge>
        </IconButton>
        <p>Notifications</p>
      </MenuItem>
      <MenuItem onClick={handlePersonDetailsOpen}>
        <IconButton
          size="large"
          aria-label="account of current user"
          aria-controls="primary-search-account-menu"
          aria-haspopup="true"
          color="inherit"
        >
          <AccountCircle />
        </IconButton>
        <p>Profile</p>
      </MenuItem>
      {roleGroupId === 1 && (
      <MenuItem onClick={handleMcbsWitch}>
        <IconButton
          size="large"
          aria-label="MCB Switch"
          aria-controls="primary-search-account-menu"
          aria-haspopup="true"
          color="inherit"
        >
          <SwapVertRounded />
        </IconButton>
        <p>MCB Switch</p>
      </MenuItem>
    )}
      <MenuItem onClick={handleLogout}>
        <IconButton
          size="large"
          aria-label="account of current user"
          aria-controls="primary-search-account-menu"
          aria-haspopup="true"
          color="inherit"
        >
          <LogoutOutlinedIcon />
        </IconButton>
        <p>Log Out</p>
      </MenuItem>
    </Menu>
  );
    // Render Settings menu with button-like items and icons
  const renderSettingsMenu = (
    <Menu
      anchorEl={settingsAnchorEl}
      open={Boolean(settingsAnchorEl)}
      onClose={handleSettingsMenuClose}
      PaperProps={{
        sx: {
          borderRadius: "8px", // Rounded corners
          boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)", // Subtle shadow
        },
      }}
    >
      <MenuItem
        onClick={handleDownloadUserManual}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          padding: "8px 16px",
          borderRadius: "4px",
          "&:hover": {
            backgroundColor: colors.primary[400], // Hover effect
          },
        }}
      >
        <ListItemIcon>
          <UserManualIcon fontSize="small" />
        </ListItemIcon>
        <Typography variant="body1">Download User Manual</Typography>
      </MenuItem>
      <MenuItem
        onClick={handleDownloadMobileApp}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          padding: "8px 16px",
          borderRadius: "4px",
          "&:hover": {
            backgroundColor: colors.primary[400], // Hover effect
          },
        }}
      >
        <ListItemIcon>
          <MobileAppIcon fontSize="small" />
        </ListItemIcon>
        <Typography variant="body1">Download Mobile App</Typography>
      </MenuItem>
    </Menu>
  );

  return (
    <Box sx={{ flexGrow: 1 }}>
     {/* Campaign Banner */}
     {/* {showCampaign && (
        <CampaignBanner>
          <Typography variant="body1">
            New Mobile App Release (v1.1.0)! Download now for the latest features.
          </Typography>
          <Box>
            <Button
              variant="contained"
              color="secondary"
              size="small"
              startIcon={<MobileAppIcon />}
              onClick={handleDownloadMobileApp}
              sx={{ mr: 1 }}
            >
              Download
            </Button>
            <IconButton
              size="small"
              onClick={() => {
                setShowCampaign(false);
                localStorage.setItem("hasShownCampaignBanner", "true");
                console.log("Banner dismissed manually");
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </CampaignBanner>
      )} */}
      <AppBar
        position="static"
        sx={{
          backgroundColor: colors.primary[500],
          boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)" /* Modern shadow */,
        }}
      >
        <Toolbar
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: { xs: 2, md: 3 },
          }}
        >
          <IconButton
            edge="start"
            color="inherit"
            aria-label="settings"
            onClick={handleSettingsMenuOpen}
            sx={{
              "&:hover": { backgroundColor: alpha("#fff", 0.12) },
            }}
          >
            <SettingsIcon />
          </IconButton>

          <Box sx={{ flexGrow: 1 }} />

          <Stack
            direction="row"
            alignItems="center"
            spacing={0.5}
            sx={{
              display: { xs: "none", md: "flex" },
              "& .MuiIconButton-root:hover": {
                backgroundColor: alpha("#fff", 0.12),
              },
            }}
          >
            {trialDays > 0 && (
              <TrialDaysBadge>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {trialDays} {trialDays === 1 ? "Day" : "Days"} Left
                </Typography>
                <PremiumButton size="small" startIcon={<ArrowCircleRightRounded />}>
                  Go Premium
                </PremiumButton>
              </TrialDaysBadge>
            )}
            <IconButton
              size="large"
              color="inherit"
              onClick={colorMode.toggleColorMode}
              sx={{
                transition: "color 0.3s ease",
                "&:hover": {
                  color: theme.palette.mode === "dark" ? "#ffc107" : "#ffeb3b",
                },
              }}
            >
              {theme.palette.mode === "dark" ? (
                <LightModeIcon />
              ) : (
                <DarkModeIcon />
              )}
            </IconButton>

            {parseInt(decodedToken.isAdmin) === 1 ? (
              <>
                <Tooltip title="MCB Switch">
                  <IconButton onClick={handleMcbsWitch} color="inherit">
                    <SwapVertRounded />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Logout">
                  <IconButton onClick={handleLogout} color="inherit">
                    <LogoutOutlinedIcon />
                  </IconButton>
                </Tooltip>
              </>
            ) : (
              <Tooltip title="Logout">
                <IconButton onClick={handleLogout} color="inherit">
                  <LogoutOutlinedIcon />
                </IconButton>
              </Tooltip>
            )}

            <NotificationBell />
            <IconButton
              size="large"
              aria-label="show 4 new mails"
              color="inherit"
              onClick={handleNotificationClick}
            >
              <Badge badgeContent={notifications.count} sx={{ "& .MuiBadge-badge": { backgroundColor: colors.redAccent[500], color: "#fff" } }}>
                <MailIcon />
              </Badge>
            </IconButton>
            <IconButton
              size="large"
              aria-label="show 17 new notifications"
              color="inherit"
              onClick={handleNotificationClick}
            >
              <Badge badgeContent={notifications.count} sx={{ "& .MuiBadge-badge": { backgroundColor: colors.redAccent[500], color: "#fff" } }}>
                <NotificationsIcon />
              </Badge>
            </IconButton>
            <Tooltip title="Profile">
              <IconButton onClick={handleIconClick} color="inherit">
                <AccountCircle />
              </IconButton>
            </Tooltip>
          </Stack>

          <Box sx={{ display: { xs: "flex", md: "none" } }}>
            <IconButton
              size="large"
              aria-label="show more"
              aria-controls={mobileMenuId}
              aria-haspopup="true"
              onClick={handleMobileMenuOpen}
              color="inherit"
              sx={{ "&:hover": { backgroundColor: alpha("#fff", 0.12) } }}
            >
              <MoreIcon />
            </IconButton>
          </Box>
        </Toolbar>
        <Popover
          id={id}
          open={open}
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
        >
          <NotificationsPopoverContent
            notifications={notifications}
            colors={colors}
            theme={theme}
          />
        </Popover>
      </AppBar>
      {renderMobileMenu}
      {renderSettingsMenu} 
      {renderMenu}
      {/* Loading Wall Animation */}
      {isLoading && (
        <Dialog
          open={isLoading}
          PaperProps={{ style: { backgroundColor: "transparent", boxShadow: "none" } }}
        >
          <DialogContent
            style={{ display: "flex", justifyContent: "center", alignItems: "center" }}
          >
            <CircularProgress size={60} thickness={4} style={{ color: colors.primary[500] }} />
          </DialogContent>
        </Dialog>
      )}

      {/* Error Dialog */}
      <Dialog open={!!error} onClose={handleCloseErrorDialog}>
        <DialogTitle>Error</DialogTitle>
        <DialogContent>
          <Alert severity="error">{error}</Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseErrorDialog} color="primary">
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Topbar;
